import { Platform } from 'react-native';
import { updateUser as updateUserSupabase, getUserById } from './database';

// Only import Notifications on native platforms
let Notifications = null;
if (Platform.OS !== 'web') {
  Notifications = require('expo-notifications');
  
  // Configure how notifications are handled when app is in foreground
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: true,
      shouldSetBadge: true,
    }),
  });
}

class NotificationService {
  constructor() {
    this.expoPushToken = null;
    this.notificationListener = null;
    this.responseListener = null;
  }

  /**
   * Request permission and get push token
   */
  async registerForPushNotifications() {
    // Skip on web
    if (Platform.OS === 'web' || !Notifications) {
      console.log('⚠️ Push notifications not available on web');
      return null;
    }

    try {
      // Request permissions
      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;

      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }

      if (finalStatus !== 'granted') {
        console.log('❌ Push notification permission denied');
        return null;
      }

      // Get the push token
      const token = (await Notifications.getExpoPushTokenAsync()).data;
      console.log('✅ Expo push token:', token);
      this.expoPushToken = token;

      // Configure Android channel (required for Android)
      if (Platform.OS === 'android') {
        Notifications.setNotificationChannelAsync('default', {
          name: 'default',
          importance: Notifications.AndroidImportance.MAX,
          vibrationPattern: [0, 250, 250, 250],
          lightColor: '#7FB069',
        });
      }

      return token;
    } catch (error) {
      console.error('Error registering for push notifications:', error);
      return null;
    }
  }

  /**
   * Save push token to user profile in database
   */
  async savePushTokenToProfile(userId, token) {
    try {
      await updateUserSupabase(userId, { push_token: token });
      console.log('✅ Push token saved to user profile');
    } catch (error) {
      console.error('Error saving push token:', error);
    }
  }

  /**
   * Listen for incoming notifications
   */
  addNotificationReceivedListener(callback) {
    if (Platform.OS === 'web' || !Notifications) return null;
    this.notificationListener = Notifications.addNotificationReceivedListener(callback);
  }

  /**
   * Listen for notification taps
   */
  addNotificationResponseReceivedListener(callback) {
    if (Platform.OS === 'web' || !Notifications) return null;
    this.responseListener = Notifications.addNotificationResponseReceivedListener(callback);
  }

  /**
   * Remove listeners
   */
  removeListeners() {
    if (Platform.OS === 'web' || !Notifications) return;
    if (this.notificationListener) {
      Notifications.removeNotificationSubscription(this.notificationListener);
    }
    if (this.responseListener) {
      Notifications.removeNotificationSubscription(this.responseListener);
    }
  }

  /**
   * Schedule a local notification (for testing or in-app notifications)
   */
  async scheduleLocalNotification(title, body, data = {}) {
    if (Platform.OS === 'web' || !Notifications) {
      console.log('⚠️ Local notifications not available on web');
      return;
    }
    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data,
        sound: true,
      },
      trigger: null, // Immediately
    });
  }

  /**
   * Send push notification to a specific user
   * This would typically be done from a backend, but for MVP we can use Expo's push service directly
   */
  async sendPushNotification(recipientPushToken, title, body, data = {}) {
    const message = {
      to: recipientPushToken,
      sound: 'default',
      title,
      body,
      data,
    };

    try {
      const response = await fetch('https://exp.host/--/api/v2/push/send', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Accept-encoding': 'gzip, deflate',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(message),
      });

      const result = await response.json();
      console.log('✅ Push notification sent:', result);
      return result;
    } catch (error) {
      console.error('❌ Error sending push notification:', error);
      return null;
    }
  }

  /**
   * Get push token for a user by their ID
   */
  async getUserPushToken(userId) {
    try {
      const userData = await getUserById(userId);
      return userData?.push_token || null;
    } catch (error) {
      console.error('Error fetching user push token:', error);
      return null;
    }
  }

  /**
   * Send message notification to a user
   */
  async notifyNewMessage(recipientUserId, senderName, messageContent) {
    try {
      const recipientPushToken = await this.getUserPushToken(recipientUserId);
      
      if (!recipientPushToken) {
        console.log('⚠️ Recipient has no push token registered');
        return;
      }

      await this.sendPushNotification(
        recipientPushToken,
        `New message from ${senderName}`,
        messageContent,
        {
          type: 'message',
          senderId: recipientUserId,
        }
      );
    } catch (error) {
      console.error('Error sending message notification:', error);
    }
  }

  /**
   * Send friend request notification to a user
   */
  async notifyFriendRequest(recipientUserId, senderName) {
    try {
      const recipientPushToken = await this.getUserPushToken(recipientUserId);

      if (!recipientPushToken) {
        console.log('⚠️ Recipient has no push token registered');
        return;
      }

      await this.sendPushNotification(
        recipientPushToken,
        'New friend request',
        `${senderName || 'Someone'} sent you a friend request`,
        {
          type: 'friend_request',
          recipientUserId,
        }
      );
    } catch (error) {
      console.error('Error sending friend request notification:', error);
    }
  }

  /**
   * Get notification badge count
   */
  async getBadgeCount() {
    if (Platform.OS === 'web' || !Notifications) return 0;
    return await Notifications.getBadgeCountAsync();
  }

  /**
   * Set notification badge count
   */
  async setBadgeCount(count) {
    if (Platform.OS === 'web' || !Notifications) return;
    await Notifications.setBadgeCountAsync(count);
  }

  /**
   * Clear all notifications
   */
  async clearAllNotifications() {
    if (Platform.OS === 'web' || !Notifications) return;
    await Notifications.dismissAllNotificationsAsync();
  }
}

export default new NotificationService();

